import { FC, useContext, useMemo } from 'react'
import { useQuery } from 'react-query'
import {
  createResponseContext,
  initialQueryResponse,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
} from '../../../../../../_metronic/helpers'
import { useQueryRequest } from './QueryRequestProvider'
import { useModal } from '../../../../../components'
import { getAtendimentosByNota, getAtendimentosByNotaByProfissional } from './_requests'
import { NotaAtendimentos } from './_models'
import { useLocation, useParams } from 'react-router-dom'

const QueryResponseContext = createResponseContext<NotaAtendimentos>(initialQueryResponse)

/**
 * A nota vem inteira (dados + `atendimentos`), sem paginação: o resumo e a tabela usam o
 * mesmo `responseSemPaginacao`.
 */
export const QueryResponseProvider: FC<WithChildren> = ({ children }) => {
  const { isOpen } = useModal()
  const { state } = useQueryRequest()
  const { idNota } = useParams<{ idNota: string }>()
  const location = useLocation()

  const query = useMemo(() => stringifyRequestQuery(state), [state])

  const isProfissionalRoute = location.pathname.includes('/profissional')

  // A chave inclui a nota: sem isso, notas diferentes dividiam o cache.
  const {
    isFetching,
    isError,
    refetch,
    data: responseSemPaginacao,
  } = useQuery(
    [QUERIES.NOTAS_ATENDIMENTOS_LIST, idNota, isProfissionalRoute],
    () =>
      isProfissionalRoute
        ? getAtendimentosByNotaByProfissional(Number(idNota))
        : getAtendimentosByNota(Number(idNota)),
    {
      cacheTime: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: !isOpen && Boolean(idNota),
    }
  )

  return (
    <QueryResponseContext.Provider
      value={{ isLoading: isFetching, isError, refetch, responseSemPaginacao, query }}
    >
      {children}
    </QueryResponseContext.Provider>
  )
}

export const useQueryResponse = () => useContext(QueryResponseContext)
